import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, TrendingUp, BarChart3, CheckCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

export function MerchantAnalyticsPanel({ analytics }) {
  if (!analytics) return null;

  return (
    <div className="mb-8 space-y-6" data-testid="analytics-panel">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><p className="text-3xl font-bold text-blue-600">{analytics.this_month?.resgates || 0}</p><p className="text-xs text-slate-500 mt-1">Resgates este mês</p></CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><p className="text-3xl font-bold text-green-600">{analytics.this_month?.cupons_criados || 0}</p><p className="text-xs text-slate-500 mt-1">Cupons criados</p></CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><p className="text-3xl font-bold text-amber-600">{analytics.this_month?.validacoes || 0}</p><p className="text-xs text-slate-500 mt-1">Validacoes</p></CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><div className="flex items-center justify-center gap-1"><Star className="h-5 w-5 fill-amber-400 text-amber-400" /><p className="text-3xl font-bold text-slate-900">{analytics.avg_rating || '-'}</p></div><p className="text-xs text-slate-500 mt-1">Avaliacao media</p></CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><p className="text-3xl font-bold text-slate-700">{analytics.total_ratings || 0}</p><p className="text-xs text-slate-500 mt-1">Total avaliacoes</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200" data-testid="monthly-evolution-chart">
          <CardHeader><CardTitle className="text-lg text-slate-900 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-blue-500" /> Evolucao Mensal</CardTitle></CardHeader>
          <CardContent>
            {analytics.monthly_evolution?.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={analytics.monthly_evolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} labelStyle={{ fontWeight: 'bold', color: '#1e293b' }} />
                  <Area type="monotone" dataKey="resgates" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} strokeWidth={2} name="Resgates" />
                  <Area type="monotone" dataKey="validacoes" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} strokeWidth={2} name="Validacoes" />
                  <Area type="monotone" dataKey="cupons_criados" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={2} name="Cupons Criados" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-400 text-center py-12">Dados insuficientes para exibir grafico</p>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          {analytics.top_coupons?.length > 0 && (
            <Card className="bg-white border border-slate-200" data-testid="top-coupons-card">
              <CardHeader><CardTitle className="text-lg text-slate-900 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-green-500" /> Top Cupons</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics.top_coupons.map((c, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-bold text-slate-400 w-5">{i + 1}.</span>
                        <span className="text-sm text-slate-900 truncate">{c.title}</span>
                        {c.active && <CheckCircle className="h-3 w-3 text-green-500 shrink-0" />}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-red-50 text-red-600 border-red-200 text-xs">{c.discount}%</Badge>
                        <span className="text-sm font-bold text-blue-600">{c.uses} usos</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {analytics.categories?.length > 0 && (
            <Card className="bg-white border border-slate-200" data-testid="categories-breakdown-card">
              <CardHeader><CardTitle className="text-lg text-slate-900">Categorias</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={Math.min(analytics.categories.length * 36 + 20, 180)}>
                  <BarChart data={analytics.categories.slice(0, 5)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} stroke="#94a3b8" width={120} />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="resgates" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Resgates" />
                    <Bar dataKey="cupons" fill="#10b981" radius={[0, 4, 4, 0]} name="Cupons" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
